/**
 * Pure decision for what the homepage should render, given the live
 * getHomepageData query, the iPad offline cache, and the current effective user.
 *
 * getHomepageData is anonymized per *effective* user but reuses the same React
 * Query key across an impersonation switch (the viewer rides in a header), so
 * `query.data` can momentarily hold the PREVIOUS viewer's payload. This resolver
 * keeps three concerns straight, and is unit-tested because the inline version
 * accreted several subtle bugs (rendering another viewer's list; pinning the
 * spinner when a post-switch refetch failed):
 *
 *   1. Never expose a payload that belongs to a different viewer.
 *   2. Prefer the correctly-scoped offline cache when the live data isn't usable.
 *   3. Show loading only while genuinely working toward this viewer's data — and
 *      never spin forever: once the query has errored with nothing renderable,
 *      surface the error so it can recover.
 */
export type HomepageViewInput<T> = {
  /** Raw getHomepageData payload (may belong to any viewer). */
  queryData: T | undefined;
  /** Offline cache for the CURRENT effective user (iPad only; null otherwise). */
  cachedData: T | null;
  /** The current effective (viewer) user id, or null before identity resolves. */
  effectiveUserId: string | null;
  isQueryLoading: boolean;
  isQueryError: boolean;
};

export type HomepageView<T> = {
  data: T | undefined;
  isLoading: boolean;
  isError: boolean;
};

export function resolveHomepageView<
  T extends { user?: { id?: string | null } | null },
>(input: HomepageViewInput<T>): HomepageView<T> {
  const queryViewerId = input.queryData?.user?.id ?? null;
  // "Stale" only when the payload clearly belongs to ANOTHER viewer (has an id
  // that differs). A payload with no user id (e.g. a user with no rounds) is not
  // another viewer's data — render it rather than treating it as stale.
  const staleViewer =
    queryViewerId != null && queryViewerId !== input.effectiveUserId;
  const liveData =
    input.queryData != null && !staleViewer ? input.queryData : undefined;

  const data = liveData ?? input.cachedData ?? undefined;

  // Loading while nothing is renderable for this viewer and the query is still
  // working toward it — including the post-impersonation window where RQ still
  // holds the previous viewer's payload and is refetching. The `!isQueryError`
  // guard is the key fix: if that refetch fails, fall through to the error state
  // instead of spinning forever on the lingering stale payload.
  const isLoading =
    !data && !input.isQueryError && (input.isQueryLoading || staleViewer);
  const isError = !data && input.isQueryError;

  return { data, isLoading, isError };
}
